<template>
	<v-card
		class="resource-card-shell sgnl-mx-0 sgnl-paragraph-s-regular"
		variant="outlined"
		min-width="300px"
		data-test="connected-repository-card"
	>
		<div
			v-if="repository && repoDetailsFromRepoHost"
			class="resource-card"
		>
			<div class="resource-card__title sgnl-flex sgnl-align-center">
				<div class="resource-card__title-name sgnl-flex sgnl-align-center">
					<GitRepo24 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
					<a
						class="repo-title sgnl-paragraph-m-medium"
						:href="repoDetailsFromRepoHost.html_url"
						target="_blank"
					>
						{{ repoDetailsFromRepoHost.full_name }}
					</a>
				</div>
				<v-tooltip
					v-if="repoConfigFileData && repoConfigFileWarnings.length === 0"
					open-delay="500"
				>
					<template v-slot:activator="{ props }">
						<a
							v-bind="props"
							class="config-file-icon sgnl-flex"
							:href="`${repository.RemoteURL}/blob/${repository.Branch}/${repoConfigFilePath}`"
							target="_blank"
						>
							<DocumentConfiguration20 />
						</a>
					</template>
					<span> Detected repository configuration file '{{ repoConfigFilePath }}'. </span>
				</v-tooltip>
				<v-tooltip
					v-else-if="repoConfigFileData && repoConfigFileWarnings.length != 0"
					open-delay="500"
					max-width="600px"
				>
					<template v-slot:activator="{ props }">
						<a
							v-bind="props"
							class="config-file-icon sgnl-flex"
							:href="`${repository.RemoteURL}/blob/${repository.Branch}/${repoConfigFilePath}`"
							target="_blank"
						>
							<DocumentUnknown20 class="sgnl-icon-color-warning" />
						</a>
					</template>
					<span>
						Detected repository configuration file with warnings:
						<br />
						<span
							v-for="warning in repoConfigFileWarnings"
							:key="warning"
						>
							{{ warning }} <br />
						</span>
					</span>
				</v-tooltip>
				<v-tooltip
					v-else-if="repoConfigFileError"
					open-delay="500"
				>
					<template v-slot:activator="{ props }">
						<a
							v-bind="props"
							class="config-file-icon sgnl-flex"
							:href="`${repository.RemoteURL}/blob/${repository.Branch}/${repoConfigFilePath}${
								repoConfigFileError.linePos && repoConfigFileError.linePos.length > 0
									? '#L' + repoConfigFileError.linePos[0].line
									: ''
							}`"
							target="_blank"
						>
							<DocumentUnknown20 class="sgnl-icon-color-error" />
						</a>
					</template>
					<span>
						Our system encountered a YAML error while parsing the configuration file
						{{ repoConfigFilePath }}. Please make sure the file is valid YAML. You can find more details of
						the error encountered during the parsing, below.
						<br />
						Error: <span class="yaml-error-message">{{ repoConfigFileError.message }}</span>
					</span>
				</v-tooltip>
			</div>
			<div class="resource-card__description">
				<div class="repo-description sgnl-paragraph-s-regular">
					{{ repoDetailsFromRepoHost.description }}
				</div>
				<!-- Topics hidden until styled -->
				<v-row
					v-if="false"
					no-gutters
					class="sgnl-paragraph-s-regular sgnl-mt-4 sgnl-ml-0"
				>
					<v-col>
						<template v-if="repoDetailsFromRepoHost?.topics">
							<v-chip
								v-for="topic in (repoDetailsFromRepoHost?.topics ?? []).slice(0, 3)"
								:key="topic"
								variant="outlined"
								size="x-small"
								color="info"
								class="sgnl-mx-0"
								>{{ topic }}</v-chip
							>
							<span
								v-if="(repoDetailsFromRepoHost?.topics ?? []).slice(3).length > 0"
								class="sgnl-caption-regular"
							>
								+ {{ (repoDetailsFromRepoHost?.topics ?? []).slice(3).length }} more
							</span>
						</template>
					</v-col>
				</v-row>
			</div>
			<div class="resource-card__config sgnl-flex sgnl-flex-wrap sgnl-align-center sgnl-gap-8">
				<v-menu max-height="300px">
					<template v-slot:activator="{ props: menuProps }">
						<v-btn
							v-bind="menuProps"
							variant="outlined"
							size="small"
							class="btn-secondary-small"
							:class="{ 'btn-secondary-small--active': repository.Branch }"
							:loading="branchUpdateStatus.loading"
							data-test="branch-selector-chip"
						>
							<Branch16 />
							{{ repository.Branch ?? "Default" }}
							<ChevronDown16 />
							<v-tooltip activator="parent">
								<span>Working Branch</span>
								<div>{{ repository.Branch }} @ {{ repository.Commit.slice(0, 8) }}</div>
								<div v-if="branchUpdateStatus.error">- {{ branchUpdateStatus.message }}</div>
							</v-tooltip>
						</v-btn>
					</template>
					<v-list>
						<v-list-item
							v-for="branch in repositoryBranchesResponse"
							:key="branch.name"
							link
							density="compact"
							@click="setWorkingBranch(branch.name, '')"
						>
							<v-list-item-title class="sgnl-m-0 sgnl-p-0 sgnl-paragraph-s-regular">{{
								branch.name
							}}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
				<v-btn
					variant="outlined"
					size="small"
					class="btn-secondary-small"
					:class="{
						'btn-secondary-small--active': coreChipStyle.color === 'primary',
						'btn-secondary-small--warning': coreChipStyle.color === 'warning',
					}"
					:loading="coreUpdateStatus.loading"
					@click="openSelectCoreDialog"
					data-test="core-selector-chip"
				>
					<Chip16 />
					{{ activeCore?.Name }}
					<SubtractFilled16
						v-if="activeCore?.Deprecated"
						class="sgnl-icon-color-error"
					/>
					<ChevronDown16 />
					<v-tooltip activator="parent">{{
						activeCore?.Deprecated ? "Active Core is deprecated — select a new core" : "Active Core"
					}}</v-tooltip>
				</v-btn>
				<v-btn
					variant="outlined"
					size="small"
					:loading="buildDirectoryUpdateStatus.loading"
					@click="openDirectoryTree"
					data-test="build-directory-selector-chip"
				>
					<FolderShared16 />
					{{ buildDirectoryPrettyString }}
					<ChevronDown16 />
					<v-tooltip activator="parent">Build Directory</v-tooltip>
				</v-btn>
				<v-btn
					variant="outlined"
					size="small"
					class="btn-secondary-small"
					:class="{ 'btn-secondary-small--active': !!repository.Arguments }"
					:loading="runArgumentsUpdateStatus.loading"
					@click="openRunArgumentsDialog"
					data-test="run-argument-selector-chip"
				>
					<Terminal16 />
					<ChevronDown16 />
					<v-tooltip activator="parent">Command-Line Arguments</v-tooltip>
				</v-btn>
				<v-btn
					variant="outlined"
					size="small"
					class="btn-secondary-small"
					:class="{ 'btn-secondary-small--active': dataSourceChipStyle.color === 'primary' }"
					:loading="dataSourceUpdateStatus.loading"
					@click="openMountDataSourcesDialog"
					data-test="data-source-selector-chip"
				>
					<component :is="dataSourceChipStyle.icon" />
					<ChevronDown16 />
					<v-tooltip activator="parent">
						<span>Data Sources</span><br />
						<span>State: {{ dataSourceChipStyle.tooltip }}</span>
					</v-tooltip>
				</v-btn>
			</div>
			<div class="resource-card__footer">
				<div class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8">
					<v-chip
						variant="outlined"
						class="badge-muted copy-id-chip"
						data-test="task-card-task-id-value"
					>
						Repository ID: <span class="truncated">{{ repositoryID }}</span>
						<CopyToClipboardButton
							:value-to-copy="repositoryID"
							value-name="Repository ID"
						/>
					</v-chip>
					<span class="info-badges sgnl-inline-flex sgnl-align-center sgnl-flex-wrap">
						<span class="repo-badge">
							<Locked16 v-if="repoDetailsFromRepoHost.visibility === 'private'" />
							<Earth16 v-else />
							{{ capitalize(repoDetailsFromRepoHost.visibility) }}
						</span>
						<span
							v-if="repoDetailsFromRepoHost.license"
							class="repo-badge"
						>
							<Scales16 />
							{{ repoDetailsFromRepoHost.license.name }}
						</span>
						<span class="repo-badge">
							<Calendar16 />
							{{ formatDate(repoDetailsFromRepoHost.updated_at) }}
						</span>
						<span
							v-if="repoDetailsFromRepoHost.forks_count"
							class="repo-badge"
						>
							<Fork16 />
							{{ repoDetailsFromRepoHost.forks_count }}
						</span>
						<span
							v-if="repositoryBranchesResponse"
							class="repo-badge"
						>
							<Branch16 />
							{{ repositoryBranchesResponse.length }}
						</span>
					</span>
				</div>
				<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
					<LimitableActionButton
						variant="flat"
						color="primary"
						:loading="activeTask?.Status ? isTaskActive(activeTask.Status) : false"
						@valid-click="submitBuildRequest"
						tooltipText="Compile and Run"
						data-test="build-and-run-button"
						:limits-to-check="[UserLimitsE.ConcurrentTaskCount, UserLimitsE.DynamicInstructionCount]"
					>
						<template #btnContent>
							<PlayFilledAlt16 />
							<span>Run</span>
						</template>
					</LimitableActionButton>
					<template v-if="uiRepositoryDetailMode === false">
						<v-btn
							variant="outlined"
							@click="openRepositoryDetailsPage"
							data-test="repo-details-button"
						>
							<ValueVariable16 />
							<v-tooltip activator="parent">Discover Variables</v-tooltip>
						</v-btn>
					</template>
					<v-btn
						variant="outlined"
						:href="`https://github.dev/${repoDetailsFromRepoHost.full_name}`"
						target="_blank"
					>
						<v-icon icon="mdi-microsoft-visual-studio-code" />
						<v-tooltip activator="parent">Edit on github.dev</v-tooltip>
					</v-btn>
					<template v-if="uiRepositoryDetailMode === false">
						<v-btn
							variant="outlined"
							@click="openExecutionLog"
							:disabled="!hasTerminatedTaskOrBuild"
							data-test="open-execution-log-button"
						>
							<ExecutableProgram16 />
							<v-tooltip activator="parent">Open Execution Log</v-tooltip>
						</v-btn>
						<v-spacer />
						<v-btn
							variant="outlined"
							color="error"
							:loading="repoDisconnectStatus.loading"
							@click="disconnectRepository"
							data-test="disconnect-repo-button"
						>
							<TrashCan16 />
							<v-tooltip activator="parent">Disconnect</v-tooltip>
						</v-btn>
					</template>
				</div>
			</div>
			<div
				v-if="showBuildBar"
				class="resource-card__build-bar"
			>
				<v-expand-transition>
					<BuildBar
						v-show="showBuildBar"
						:taskID="activeTask?.TaskID"
						:taskStatus="activeTask?.Status"
						:taskAccessState="mergedTaskAccessState"
						:activeTask="activeTask"
						:buildID="activeBuild?.BuildID"
						:buildStatus="activeBuild?.Status"
						:buildAccessState="mergedBuildAccessState"
						:activeBuild="activeBuild"
						:showViewTask="true"
						@cancel-task="cancelActiveTask"
						@cancel-build="cancelActiveBuild"
						@retry-task-output="retryTaskOutput"
						@retry-build-output="retryBuildOutput"
					/>
				</v-expand-transition>
			</div>
		</div>
		<div
			v-else-if="repoAccessState.error"
			class="error-state"
		>
			<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
				<GitRepo24 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
				<span class="repo-title sgnl-paragraph-m-medium">
					{{ repoPlaceholderName }}
				</span>
			</div>
			<div class="error-state__details">
				<div class="sgnl-p-12 sgnl-flex sgnl-align-start repo-error-message sgnl-text-color-warning sgnl-gap-8">
					<ErrorOutline16 class="repo-error-icon sgnl-icon-align-text" />
					<span>{{ repoAccessState.message }}</span>
				</div>
				<v-btn
					v-if="uiRepositoryDetailMode === false"
					class="sgnl-align-self-start"
					variant="outlined"
					color="error"
					:loading="repoDisconnectStatus.loading"
					@click="disconnectRepository"
					data-test="disconnect-repo-button"
				>
					<TrashCan16 />
					<span>Disconnect</span>
				</v-btn>
			</div>
		</div>
		<div
			v-else
			class="resource-card"
		>
			<div class="resource-card__title-name sgnl-flex sgnl-align-center">
				<GitRepo24 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
				<span class="repo-title sgnl-paragraph-m-medium">
					{{ repoPlaceholderName }}
				</span>
			</div>
			<ConnectedRepositoryCardSkeleton />
		</div>
		<!-- v-if to force re-mount -->
		<RunArgumentsDialog
			v-if="showRunArgumentsDialog"
			:showDialog="showRunArgumentsDialog"
			:initialRunArguments="repository?.Arguments"
			@close-dialog="closeRunArgumentsDialog"
			@set-arguments="setRunArguments"
		/>

		<SelectCoreDialog
			v-if="showSelectCoreDialog"
			:showDialog="showSelectCoreDialog"
			:minimumCore="repoConfigFileData?.MinimumCore"
			:activeCore="activeCore"
			@close-dialog="closeSelectCoreDialog"
			@set-core="setActiveCoreID"
		/>

		<RepositoryBuildDirectoryDialog
			v-if="showDirectoryDialog"
			v-model:showDialog="showDirectoryDialog"
			:selected-directory-path="selectedBuildDirectory"
			@update:selectedDirectoryPath="selectedBuildDirectoryUpdateHandler"
			:repository-full-name="repositoryFullName"
			:sha-name="repository?.Branch ?? repository?.Commit"
		/>

		<!-- FIXME -->
		<MountDataSourcesDialog
			v-if="showMountDataSourcesDialog"
			:showDialog="showMountDataSourcesDialog"
			:initialMountConfig="chosenDataSources ? chosenDataSources[0] : undefined"
			@close-dialog="closeMountDataSourcesDialog"
			@set-data-sources="setMountConfig"
			@reset-data-sources="setMountConfig"
			@remove-data-sources="setMountConfig"
		/>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType, provide, ref } from "vue";
import { useCancelWithFeedback, CancelFeedbackKey } from "@/composables/useCancelWithFeedback";
import ErrorOutline16 from "@carbon/icons-vue/es/error--outline/16";
import DataCheck16 from "@carbon/icons-vue/es/data--check/16";
import DataError16 from "@carbon/icons-vue/es/data--error/16";
import SettingsServices16 from "@carbon/icons-vue/es/settings--services/16";

// Types
import type { Endpoints } from "@octokit/types";
import {
	BuildBusEvent,
	FileTree,
	RemoteAccessState,
	ResultsPanelTab,
	SignaloidConfigFile,
	SignaloidConfigFileMinimumCoreOptions,
	TaskBusEvent,
} from "@/types/general";
import { isTaskActive, isTaskTerminated, Task, TaskStatusE } from "@/types/api/tasks";
import { DataSource, LocalMountConfig } from "@/types/api/dataSources";
import { Core } from "@/types/api/cores";
import { Repository } from "@/types/api/repositories";
import { TraceVariable, TraceVariableWithTraceType } from "@/types/api/nodes";
import { UserLimitsE } from "@/js/tierconfig";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { capitalize, shortenID } from "@/js/utilities";
import * as util from "@/js/utilities";

// Libraries
import moment from "moment";
import YAML, { YAMLError, YAMLParseError } from "yaml";
import axios from "axios";
import * as Sentry from "@sentry/vue";

// Utilities
import * as githubClient from "@/js/githubClient";
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import * as dsUtil from "@/components/DataSources/utilities";
import { isAxiosTimeoutError, monitoringCaptureError } from "@/plugins/monitoring";
import { base64Decode } from "@/js/utilities";

// Components
import BuildBar from "@/components/Common/BuildBar.vue";
import RunArgumentsDialog from "@/components/RunArgumentsDialog.vue";
import SelectCoreDialog from "@/components/SelectCoreDialog.vue";
import MountDataSourcesDialog from "@/components/MountDataSourcesDialog.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import RepositoryBuildDirectoryDialog from "./RepositoryBuildDirectoryDialog.vue";
import ConnectedRepositoryCardSkeleton from "./ConnectedRepositoryCardSkeleton.vue";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import Editor from "@/components/Editor.vue";
import Scales16 from "@carbon/icons-vue/es/scales/16";
import PlayFilledAlt16 from "@carbon/icons-vue/es/play--filled--alt/16";
import ExecutableProgram16 from "@carbon/icons-vue/es/executable-program/16";
import ValueVariable16 from "@carbon/icons-vue/es/value--variable/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Terminal16 from "@carbon/icons-vue/es/terminal/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";
import DocumentConfiguration20 from "@carbon/icons-vue/es/document--configuration/20";
import DocumentUnknown20 from "@carbon/icons-vue/es/document--unknown/20";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import Branch16 from "@carbon/icons-vue/es/branch/16";
import Fork16 from "@carbon/icons-vue/es/fork/16";
import FolderShared16 from "@carbon/icons-vue/es/folder--shared/16";
import SubtractFilled16 from "@carbon/icons-vue/es/subtract--filled/16";
import Calendar16 from "@carbon/icons-vue/es/calendar/16";
import GitRepo24 from "@carbon/icons-vue/es/git--repo/24";
import Earth16 from "@carbon/icons-vue/es/earth/16";
import Locked16 from "@carbon/icons-vue/es/locked/16";
// Stores
import { mapState } from "pinia";
import { useTasksStore } from "@/stores/tasks";
import { useCoresStore } from "@/stores/cores";
import { useRepositoriesStore } from "@/stores/repositories";
import { useRootStore } from "@/stores/root";
import { useGithubStore } from "@/stores/github";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useUserStore } from "@/stores/user";
import { generateGithubResponseErrorMessage } from "@/js/githubUtilities";
import { useOutputsStore } from "@/stores/outputs";
import { useEditorStore } from "@/stores/editor";
import { Build, BuildStatusE, canFetchVariables, isBuildFail, isBuildTerminated } from "@/types/api/builds";
import { shouldSkipBuildOutputForError } from "@/types/buildErrors";
import { useBuildsStore } from "@/stores/builds";

// Local Types
type ComponentData = {
	repoDetailsFromRepoHost: null | Endpoints["GET /repos/{owner}/{repo}"]["response"]["data"];
	// repositoryFromDB: Repository;
	activeTask: undefined | Partial<Task>;
	activeBuild: undefined | Partial<Build>;
	showBuildBar: boolean;
	highlightedWorkingDirectory: FileTree[];
	selectedBuildDirectory: string;
	openTrees: any[];
	directoryTreeViewItems: any[];
	repositoryBranchesResponse: undefined | Endpoints["GET /repos/{owner}/{repo}/branches"]["response"]["data"];
	showDirectoryDialog: boolean;
	showRunArgumentsDialog: boolean;
	showSelectCoreDialog: boolean;
	showMountDataSourcesDialog: boolean;
	repoAccessState: RemoteAccessState;
	coreUpdateStatus: RemoteAccessState;
	branchUpdateStatus: RemoteAccessState;
	repoDisconnectStatus: RemoteAccessState;
	runArgumentsUpdateStatus: RemoteAccessState;
	buildDirectoryUpdateStatus: RemoteAccessState;
	dataSourceUpdateStatus: RemoteAccessState;
	taskAccessState: RemoteAccessState;
	buildAccessState: RemoteAccessState;
	taskResultsFetchStatus: RemoteAccessState;
	lastTaskResults: undefined | ResultsPanelTab[];
	taskEventBusSub: undefined | Function;
	buildEventBusSub: undefined | Function;
	repoConfigFilePath: undefined | null | string;
	repoConfigFileData: undefined | null | SignaloidConfigFile;
	repoConfigFileError: undefined | YAMLError;
	repoConfigFileWarnings: Array<string>;
	lastBuildForVariableDiscovery: boolean;
	submittingTask: boolean;
};

// Global variables
const defaultWorkingDirectory: FileTree = { name: "./", path: "", type: "dir", children: [] };

type ChipStyle = {
	color: string;
	outlined: boolean;
	textColor: string;
};

export default defineComponent({
	name: "ConnectedRepositoryCard",
	components: {
		RunArgumentsDialog,
		SelectCoreDialog,
		MountDataSourcesDialog,
		RepositoryBuildDirectoryDialog,
		ConnectedRepositoryCardSkeleton,
		BuildBar,
		TooltipButton,
		LimitableActionButton,
		CopyToClipboardButton,
		Terminal16,
		Chip16,
		ErrorOutline16,
		DocumentConfiguration20,
		DocumentUnknown20,
		Scales16,
		DataCheck16,
		DataError16,
		SettingsServices16,
		ChevronDown16,
		Branch16,
		Fork16,
		FolderShared16,
		Calendar16,
		PlayFilledAlt16,
		ExecutableProgram16,
		ValueVariable16,
		TrashCan16,
		SubtractFilled16,
		GitRepo24,
		Earth16,
		Locked16,
	},
	props: {
		repositoryID: { type: String, required: true },
		repository: { type: Object as PropType<Repository>, required: true },
		initialBuildBarStatus: { type: Boolean, default: false },
		variableTraceList: {
			type: Array as PropType<TraceVariable[]>,
			default: () => [],
		},
		uiRepositoryDetailMode: { type: Boolean, default: false },
	},
	emits: {
		"clear-results": () => true,
		"repository-disconnected": (_data: { repositoryID: string }) => true,
		"repository-missing": () => true,
		"open-execution-log": (_data: { taskID: string; buildID: string }) => true,
		"repository-variables-updated": (_data: TraceVariableWithTraceType[]) => true,
		"repository-config-updated": (message: string) => typeof message === "string",
		"repository-config-update-failed": (message: string) => typeof message === "string",
	},
	data: (): ComponentData => ({
		repoDetailsFromRepoHost: null,
		// repositoryFromDB: {},	// This is now part of setup() so we don't have to check nullish-ness
		activeTask: undefined,
		activeBuild: undefined,
		showBuildBar: false,
		highlightedWorkingDirectory: [defaultWorkingDirectory],
		selectedBuildDirectory: defaultWorkingDirectory.path,
		openTrees: [],
		repositoryBranchesResponse: undefined,
		directoryTreeViewItems: [],
		showDirectoryDialog: false,
		showRunArgumentsDialog: false,
		showSelectCoreDialog: false,
		showMountDataSourcesDialog: false,
		lastTaskResults: undefined,
		taskEventBusSub: undefined,
		buildEventBusSub: undefined,
		repoAccessState: { loading: false, error: false, message: "" },
		coreUpdateStatus: { loading: false, error: false, message: "" },
		branchUpdateStatus: { loading: false, error: false, message: "" },
		buildDirectoryUpdateStatus: { loading: false, error: false, message: "" },
		runArgumentsUpdateStatus: { loading: false, error: false, message: "" },
		dataSourceUpdateStatus: { loading: false, error: false, message: "" },
		repoDisconnectStatus: { loading: false, error: false, message: "" },
		taskAccessState: { loading: false, error: false, message: "" },
		buildAccessState: { loading: false, error: false, message: "" },
		taskResultsFetchStatus: { loading: false, error: false, message: "" },
		repoConfigFileData: undefined,
		repoConfigFilePath: undefined,
		repoConfigFileError: undefined,
		repoConfigFileWarnings: [],
		lastBuildForVariableDiscovery: false,
		submittingTask: false,
	}),
	setup() {
		const rootStore = useRootStore();
		const editorStore = useEditorStore();
		const editor = ref<InstanceType<typeof Editor>>();
		const tasksStore = useTasksStore();
		const buildsStore = useBuildsStore();
		const coresStore = useCoresStore();
		const userStore = useUserStore();
		const repositoriesStore = useRepositoriesStore();
		const githubStore = useGithubStore();
		const outputsStore = useOutputsStore();
		const cancel = useCancelWithFeedback();
		provide(CancelFeedbackKey, cancel);

		return {
			rootStore,
			tasksStore,
			buildsStore,
			coresStore,
			userStore,
			repositoriesStore,
			githubStore,
			isTaskActive,
			UserLimitsE,
			shortenID,
			outputsStore,
			editorStore,
			editor,
			cancel,
		};
	},
	methods: {
		/*
		 * Utilities
		 */
		capitalize,
		formatDate(dateString) {
			return moment(dateString).format("DD MMM YYYY");
		},
		async retryTaskOutput() {
			const taskID = this.activeTask?.TaskID;
			if (!taskID) return;
			await this.outputsStore.retryOutputs({ taskID });
			// openExecutionLog re-checks store state and emits only if clean.
			this.openExecutionLog();
		},
		async retryBuildOutput() {
			const buildID = this.activeBuild?.BuildID;
			if (!buildID) return;
			await this.outputsStore.retryOutputs({ buildID });
			this.openExecutionLog();
		},

		/*
		 * Getters
		 */
		async getRepoDataFromRemote() {
			if (this.remoteURLHost !== "GitHub") {
				this.repoAccessState.message = `Non-GitHub repositories are not supported yet. This repository is hosted on ${this.remoteURLHost} and cannot be loaded. Disconnect it to remove it from your list.`;
				this.repoAccessState.error = true;
				this.repoAccessState.loading = false;
				monitoringCaptureError(new Error("non-GitHub repository rendered in card"), "getRepoDataFromRemote", {
					severity: "warning",
					extras: {
						userMessage: this.repoAccessState.message,
						remoteURLHost: this.remoteURLHost,
						repositoryFullName: this.repositoryFullName,
					},
				});
				return;
			}

			try {
				const response = await githubClient.getRepoData(this.repositoryFullName);
				this.repoDetailsFromRepoHost = response.data;

				await this.getRepoBranches();

				await this.getConfigFile();
			} catch (error) {
				if (axios.isAxiosError(error)) {
					const status = error.response?.status;
					this.repoAccessState.message = generateGithubResponseErrorMessage(
						error,
						this.githubStore.githubLoggedIn,
					);

					// Only log truly unexpected errors to Sentry
					// Don't log 4xx client errors - these are user/data issues, not code bugs
					const shouldLogToSentry = !status || status >= 500;

					if (shouldLogToSentry) {
						monitoringCaptureError(error, "getRepoDataFromRemote", {
							severity: "warning",
							extras: {
								userMessage: this.repoAccessState.message,
								status: status,
								repositoryFullName: this.repositoryFullName,
							},
						});
					}
				} else {
					// Non-axios error
					this.repoAccessState.message =
						"The request to get repository information could not be created due to an unexpected error." +
						" If this error persists, please contact support at developer-support@signaloid.com.";

					monitoringCaptureError(error, "getRepoDataFromRemote", {
						severity: "fatal",
						extras: {
							userMessage: this.repoAccessState.message,
						},
					});
				}
				this.repoAccessState.error = true;
			} finally {
				this.repoAccessState.loading = false;
			}
		},
		async getRepoBranches() {
			if (this.remoteURLHost !== "GitHub") {
				throw new Error("non-GitHub repositories are not supported");
			}
			this.branchUpdateStatus.loading = true;
			this.branchUpdateStatus.error = false;
			try {
				this.repositoryBranchesResponse = await githubClient.getAllRepoBranches(this.repositoryFullName);
			} catch (error) {
				monitoringCaptureError(error, "Fetch repository branches");
				if (axios.isAxiosError(error)) {
					this.branchUpdateStatus.message = "Could not get repository branches. ";

					// If its a github error, generate the error message
					if (error.config?.baseURL?.includes("api.github")) {
						this.branchUpdateStatus.message += generateGithubResponseErrorMessage(
							error,
							this.githubStore.githubLoggedIn,
						);
					} else if (error.response) {
						//non 2xx response
						this.branchUpdateStatus.error = true;
						if (error?.response?.status === 404 || error?.response?.status === 403) {
							this.branchUpdateStatus.message += "Repository not found or access denied.";
						}
					} else if (error.request) {
						//no response
						this.branchUpdateStatus.message += "Please check your internet connection";
					} else {
						//making request failed
						this.branchUpdateStatus.message += "Please check your internet connection";
					}
				} else {
					this.branchUpdateStatus.message += "Unknown error occurred";
				}
			} finally {
				this.branchUpdateStatus.loading = false;
			}
		},
		/*
		 * Setters
		 */
		async setWorkingBranch(branchName: string, commitHash: string) {
			if (commitHash === "") {
				commitHash = "HEAD";
			}

			const changed = branchName !== this.repository.Branch || commitHash !== this.repository.Commit;

			if (changed) {
				this.buildDirectoryUpdateStatus.loading = true;

				try {
					await this.repositoriesStore.setRepositoryWorkingBranch(
						this.repositoryID,
						branchName,
						commitHash,
						this.branchUpdateStatus,
					);

					await this.getConfigFile();

					/*
					 *	When the branch changes we have no guarantee that the same paths exist, so we should check if
					 *	the path exists, if not check it src exists. if neither exists, reset to root
					 */

					let validBuildPath = "";

					// Try to get the current build dir contents from github
					try {
						await githubClient.getRepoDirectories(
							this.repository.RemoteURL.split("github.com/")[1],
							this.repository.BuildDirectory,
							this.repository?.Branch,
						);
						validBuildPath = this.repository.BuildDirectory;
					} catch (error) {
						monitoringCaptureError(error, "Set repository working branch");
						// if errors assume that path couldn't be found
					}

					// Try to get the contexts of "./src" from github
					if (validBuildPath === "") {
						try {
							await githubClient.getRepoDirectories(
								this.repository.RemoteURL.split("github.com/")[1],
								"src",
								this.repository?.Branch,
							);
							validBuildPath = "src";
						} catch (error) {
							monitoringCaptureError(error, "Set repository working branch");
							// if errors assume that path couldn't be found
						}
					}

					// if the valid build found is different to whats in db... update
					if (validBuildPath !== this.repository.BuildDirectory) {
						this.setBuildDirectory("src", true).finally(() => {
							this.buildDirectoryUpdateStatus.loading = false;
						});
						this.directoryTreeViewItems = [];
						this.openTrees = [];
					} else {
						this.buildDirectoryUpdateStatus.loading = false;
					}
				} catch {
					// The store already populated branchUpdateStatus.message and logged via monitoringCaptureError.
					this.buildDirectoryUpdateStatus.loading = false;
					this.$emit("repository-config-update-failed", this.branchUpdateStatus.message);
				}
			}
		},
		async setActiveCoreID(coreID: string) {
			if (!this.repositoryID) {
				return;
			}

			this.closeSelectCoreDialog();

			if (coreID !== this.repository.Core) {
				try {
					await this.repositoriesStore.setRepositoryCoreID(this.repositoryID, coreID, this.coreUpdateStatus);
					// @ts-ignore
					this.$posthog?.capture("repository_settings_changed", {
						repository_id: this.repositoryID,
					});
					this.$emit("repository-config-updated", "Repository core updated successfully.");
				} catch {
					// Error is already logged inside the repo store; surface the message via the parent's snackbar.
					this.$emit("repository-config-update-failed", this.coreUpdateStatus.message);
				}
			}
		},
		async setRunArguments(data: { runArguments: string }) {
			if (!this.repositoryID) {
				return;
			}

			this.closeRunArgumentsDialog();

			if (data.runArguments !== this.repository.Arguments) {
				try {
					await this.repositoriesStore.setRepositoryRunArguments(
						this.repositoryID,
						data.runArguments,
						this.runArgumentsUpdateStatus,
					);
					this.$emit("repository-config-updated", "Repository run arguments updated successfully.");
				} catch {
					this.$emit("repository-config-update-failed", this.runArgumentsUpdateStatus.message);
				}
			}
		},

		/*
		 *	Sets the BuildDirectory of the Repository object in the SCCE API via the repositoriesStore.
		 */
		async setBuildDirectory(buildDirectory: string, silent = false) {
			if (buildDirectory !== this.repository.BuildDirectory) {
				try {
					await this.repositoriesStore.setRepositoryBuildDirectory(
						this.repositoryID,
						buildDirectory,
						this.buildDirectoryUpdateStatus,
					);
					if (!silent) {
						this.$emit("repository-config-updated", "Repository build directory updated successfully.");
					}
				} catch {
					if (!silent) {
						this.$emit("repository-config-update-failed", this.buildDirectoryUpdateStatus.message);
					}
				}
			}
		},

		openRunArgumentsDialog() {
			this.showRunArgumentsDialog = true;
		},
		closeRunArgumentsDialog() {
			this.showRunArgumentsDialog = false;
		},

		openSelectCoreDialog() {
			this.showSelectCoreDialog = true;
		},
		closeSelectCoreDialog() {
			this.showSelectCoreDialog = false;
		},

		closeDirectoryDialog() {
			this.showDirectoryDialog = false;
		},

		switchToReferenceCore() {
			const referenceCore = this.coresStore.defaultReferenceCore;
			if (referenceCore?.CoreID) {
				this.setActiveCoreID(referenceCore.CoreID);
			} else {
				console.warn("No reference core available, cannot switch to reference core");
			}
		},

		/*
		 * Functions for the directory tree
		 */
		openDirectoryTree() {
			this.showDirectoryDialog = true;
		},
		selectedBuildDirectoryUpdateHandler(newSelectedBuildDirectory: string) {
			this.selectedBuildDirectory = newSelectedBuildDirectory; // Set value itself to emulate v-model
			this.setBuildDirectory(this.selectedBuildDirectory); // then set the BuildDirectory of the actual repo
			this.closeDirectoryDialog();
		},

		async setMountConfig(mountConfig: null | undefined | LocalMountConfig) {
			if (!this.repositoryID) {
				return;
			}

			this.closeMountDataSourcesDialog();

			//TODO: Add a guard here to only update db if different
			// const newConfigIsDifferent =
			// 	this.repositoryFromDB &&
			// 	(mountConfig !== this.repositoryFromDB.DataSources ||
			// 		(mountConfig === null && this.repositoryFromDB.DataSources === []));

			try {
				await this.repositoriesStore.setRepositoryDataSources(
					this.repositoryID,
					mountConfig,
					this.dataSourceUpdateStatus,
				);
				this.$emit("repository-config-updated", "Repository data source updated successfully.");
			} catch {
				this.$emit("repository-config-update-failed", this.dataSourceUpdateStatus.message);
			}
		},
		openMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = true;
		},
		closeMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = false;
		},
		async disconnectRepository() {
			this.repoDisconnectStatus.loading = true;
			try {
				await this.repositoriesStore.disconnectFromRepository(this.repositoryID);
				setTimeout(async () => {
					this.repoDisconnectStatus.loading = false;
				}, 1000);
				this.$emit("repository-disconnected", { repositoryID: this.repositoryID });
			} catch (error) {
				monitoringCaptureError(error, "Disconnect repository");
				if (error instanceof SdkError) {
					this.repoDisconnectStatus.error = true;
					this.repoDisconnectStatus.message = "An error occurred while disconnecting from the repository.";
				} else if (axios.isAxiosError(error)) {
					this.repoDisconnectStatus.error = true;
					this.repoDisconnectStatus.message =
						"Failed to disconnect the repository. Make sure you have an active internet connection.";
				}
				this.repoDisconnectStatus.loading = false;
				this.$emit("repository-config-update-failed", this.repoDisconnectStatus.message);
			}
		},

		async submitBuildRequest() {
			this.buildAccessState = {
				loading: false,
				status: undefined,
				error: false,
				message: "",
			};
			this.taskAccessState = {
				loading: false,
				error: false,
				message: "",
			};
			this.activeBuild = undefined;
			this.activeTask = undefined;
			this.$emit("clear-results");
			this.showBuildBar = true;
			// this.variableDiscoveryInProgress = true;
			this.activeBuild = { Status: BuildStatusE.Pending };
			this.activeTask = { Status: TaskStatusE.Pending };
			try {
				const traceVariables = this.variableTraceList?.length ? this.variableTraceList : undefined;

				const coreID = this.repository?.Core ?? this.coresStore.defaultCoreID;

				if (this.activeCore?.Deprecated) {
					this.activeBuild = { Status: BuildStatusE.Error };
					this.activeTask = { Status: TaskStatusE.NotStarted };
					this.buildAccessState.error = true;
					this.buildAccessState.message = this.coresStore.deprecatedCoreBuildErrorMessage(
						this.activeCore.Name,
					);
					return;
				}

				const response = await sdk.builds.createFromRepository(this.repositoryID, {
					CoreID: coreID,
					TraceVariables: traceVariables ?? [],
				});
				this.lastBuildForVariableDiscovery = false;

				const buildResponse = await sdk.builds.getOne(response.BuildID);
				if (response.BuildID) {
					this.activeBuild = buildResponse as any;

					if (!this.activeBuild?.BuildID) {
						throw new Error("Build ID not returned from API");
					}

					console.log(`Build created ID: ${this.activeBuild?.BuildID}`);

					// @ts-ignore
					this.$posthog?.capture("build_created", {
						buildType: "SourceCode",
						buildID: this.activeBuild?.BuildID,
					});
					// @ts-ignore
					this.$posthog?.capture("repository_build_started", {
						repository_id: this.repository?.RepositoryID,
					});
					if (this.buildEventBusSub == undefined) {
						// Is this necessary?
						this.subscribeToBuildEventBus();
					}

					this.buildsStore.addToActiveBuildList(this.activeBuild as Build);
				} else {
					throw new Error("Unknown error: failed to create build on server");
				}
			} catch (error) {
				let severityLevel: Sentry.SeverityLevel = "error";
				let sentryTag = "Build failed: Unknown error";

				if (error instanceof SdkError) {
					sentryTag = "Build rejected: SDK error";
					switch (error.code) {
						case ERROR_CODES.API_NOT_FOUND:
							monitoringCaptureError(error, "Repository not found when starting build", {
								severity: "info",
							});
							// Clear optimistic Pending so the build bar doesn't stay spinning.
							this.activeBuild = undefined;
							this.activeTask = undefined;
							this.$emit("repository-missing");
							return;
						case ERROR_CODES.API_FORBIDDEN: {
							if (this.activeBuild) {
								this.activeBuild.Status = BuildStatusE.TierLimited;
							}
							if (error.message?.toLowerCase().includes("dynamic instruction")) {
								this.buildAccessState.message =
									"You have reached the monthly computation limit for your account." +
									" Please upgrade your account in https://signaloid.io/billing to run more builds." +
									" If you have further questions, please contact support at developer-support@signaloid.com.";
								this.rootStore.tierLimitEventBus.emit({
									type: TierLimitEventTypeE.LimitExceeded,
									affectedLimits: [UserLimitsE.DynamicInstructionCount],
								});
							} else {
								this.buildAccessState.message =
									"You have reached the limit of available concurrently-running builds for your account." +
									" Please upgrade your account in https://signaloid.io/billing to enable builds running concurrently." +
									" If you have further questions, please contact support at developer-support@signaloid.com.";
								this.rootStore.tierLimitEventBus.emit({
									type: TierLimitEventTypeE.LimitExceeded,
									affectedLimits: [UserLimitsE.ConcurrentBuildCount],
								});
							}
							break;
						}
						case ERROR_CODES.API_UNAUTHORIZED:
							if (this.activeBuild) {
								this.activeBuild.Status = BuildStatusE.Error;
							}
							this.buildAccessState.message =
								"Our system encountered an authorization error while starting the build." +
								" Signing out and back in will ensure you are properly authorized." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_SERVER_ERROR:
							if (this.activeBuild) {
								this.activeBuild.Status = BuildStatusE.Error;
							}
							this.buildAccessState.message =
								"Our system encountered an internal error while starting the build." +
								" Our team will receive a high-priority notification for this issue." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							severityLevel = "fatal";
							break;
						default:
							if (this.activeBuild) {
								this.activeBuild.Status = BuildStatusE.Error;
							}
							this.buildAccessState.message =
								"Our system encountered an error while starting the new build." +
								" Please try starting the build again." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;
					}
					this.buildAccessState.error = true;
				} else if (axios.isAxiosError(error)) {
					// SDK forwards raw axios errors only when there's no response (network failure).
					if (this.activeBuild) {
						this.activeBuild.Status = BuildStatusE.Error;
					}
					this.buildAccessState.error = true;
					this.buildAccessState.message =
						"Failed to start the build. Make sure you have an active internet connection.";
					sentryTag = "Build failed: Request Failed";
					severityLevel = isAxiosTimeoutError(error) ? "info" : "error";
				} else {
					if (this.activeBuild) {
						this.activeBuild.Status = BuildStatusE.Error;
					}
					this.buildAccessState.error = true;
					this.buildAccessState.message =
						"The build request could not be completed due to an unexpected error." +
						" If this error persists, please contact support at developer-support@signaloid.com.";
				}
				if (this.activeTask && !this.activeTask.TaskID) {
					this.activeTask.Status = TaskStatusE.NotStarted;
				}

				Sentry.captureException(new Error(sentryTag), {
					level: severityLevel,
					extra: { error: error, userMessage: this.buildAccessState.message },
					tags: { service: "create-build" },
				});
			}
		},
		async submitTaskRequest() {
			if (!this.activeBuild || !this.activeBuild.BuildID || this.submittingTask) {
				return;
			}
			this.submittingTask = true;
			try {
				// if the user has not selected a data source... switch to default
				const dataSources =
					this.repository?.DataSources ??
					(await dsUtil.formatDataSourcesPayload(
						this.defaultRepoMountConfig?.ResourceID,
						this.defaultRepoMountConfig?.ResourceType,
						this.defaultRepoMountConfig?.Location,
					));
				const args = this.repository.Arguments;

				const response = await sdk.tasks.createTask(this.activeBuild.BuildID, {
					Arguments: args,
					DataSources: dataSources ?? [],
				});

				if (response.TaskID) {
					this.activeTask = response as any;

					if (!this.activeTask?.TaskID) {
						throw new Error("Task ID not returned from API");
					}

					console.info(`Task created with TaskID '${this.activeTask?.TaskID}'`);

					// @ts-ignore
					this.$posthog?.capture("task_created", {
						taskType: "Repository",
						taskId: this.activeTask?.TaskID,
					});

					if (this.taskEventBusSub == undefined) {
						this.subscribeToTaskEventBus();
					}

					// @ts-ignore - FIXME: this is because active TASK is a partial type
					this.tasksStore.addToActiveTaskList(this.activeTask);
				} else {
					// TODO: handle error
					throw new Error("Unknown error occurred when creating task.");
				}
				/*
				 * At this point the task has been accepted and will be queued to be run.
				 * Any subsequent tasks will return with 403 if the concurrent task limit has been reached.
				 * The task status updates will be handled by `taskEventBusCallback`
				 */
			} catch (error) {
				monitoringCaptureError(error, "Create repository task");

				let severityLevel: Sentry.SeverityLevel = "error";
				let sentryTag = "Task failed: Unknown error";

				if (error instanceof SdkError) {
					sentryTag = "Task rejected: SDK error";
					switch (error.code) {
						case ERROR_CODES.API_NOT_FOUND:
							monitoringCaptureError(error, "Repository not found when starting task", {
								severity: "info",
							});
							this.$emit("repository-missing");
							return;
						case ERROR_CODES.API_FORBIDDEN: {
							if (this.activeTask) {
								this.activeTask.Status = TaskStatusE.TierLimited;
							}
							if (error.message?.toLowerCase().includes("dynamic instruction")) {
								this.taskAccessState.message =
									"You have reached the monthly dynamic instruction allowance for your account." +
									" Please upgrade your account in https://signaloid.io/billing to run more tasks." +
									" If you have further questions, please contact support at developer-support@signaloid.com.";
								this.rootStore.tierLimitEventBus.emit({
									type: TierLimitEventTypeE.LimitExceeded,
									affectedLimits: [UserLimitsE.DynamicInstructionCount],
								});
							} else {
								this.taskAccessState.message =
									"You have reached the limit of available concurrently-running tasks for your account." +
									" Please upgrade your account in https://signaloid.io/billing to enable tasks running concurrently." +
									" If you have further questions, please contact support at developer-support@signaloid.com.";
								this.rootStore.tierLimitEventBus.emit({
									type: TierLimitEventTypeE.LimitExceeded,
									affectedLimits: [UserLimitsE.ConcurrentTaskCount],
								});
							}
							break;
						}
						case ERROR_CODES.API_UNAUTHORIZED:
							if (this.activeTask) {
								this.activeTask.Status = TaskStatusE.Error;
							}
							this.taskAccessState.message =
								"Our system encountered an authorization error while starting the task." +
								" Signing out and back in will ensure you are properly authorized." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_SERVER_ERROR:
							if (this.activeTask) {
								this.activeTask.Status = TaskStatusE.Error;
							}
							this.taskAccessState.message =
								"Our system encountered an internal error while starting the task." +
								" Our team will receive a high-priority notification for this issue." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							severityLevel = "fatal";
							break;
						default:
							if (this.activeTask) {
								this.activeTask.Status = TaskStatusE.Error;
							}
							this.taskAccessState.message =
								"Our system encountered an error while starting the new task." +
								" Please try starting the task again." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;
					}
					this.taskAccessState.error = true;
				} else if (axios.isAxiosError(error)) {
					if (this.activeTask) {
						this.activeTask.Status = TaskStatusE.Error;
					}
					this.taskAccessState.error = true;
					this.taskAccessState.message =
						"Failed to start the task. Make sure you have an active internet connection.";
					sentryTag = "Task failed: Request Failed";
					severityLevel = isAxiosTimeoutError(error) ? "info" : "error";
				} else {
					if (this.activeTask) {
						this.activeTask.Status = TaskStatusE.Error;
					}
					this.taskAccessState.error = true;
					this.taskAccessState.message =
						"The task request could not be completed due to an unexpected error." +
						" If this error persists, please contact support at developer-support@signaloid.com.";
				}
				Sentry.captureException(new Error(sentryTag), {
					level: severityLevel,
					extra: { error: error, userMessage: this.taskAccessState.message },
					tags: { service: "create-task" },
				});
			} finally {
				this.submittingTask = false;
			}
		},
		async openExecutionLog() {
			try {
				const taskID = this.activeTask?.TaskID;
				const buildID = this.activeBuild?.BuildID;
				const taskFetchState = taskID ? this.outputsStore.getTaskOutputAccessState(taskID) : undefined;
				const buildFetchState = buildID ? this.outputsStore.getBuildOutputAccessState(buildID) : undefined;
				// If the output fetch already failed, the build bar surfaces the error + retry.
				// Don't open the dialog — it would cover the error message behind it.
				if (taskFetchState?.error || buildFetchState?.error) {
					return;
				}
				if (taskID && this.activeTask?.Status && isTaskTerminated(this.activeTask.Status)) {
					this.$emit("open-execution-log", {
						taskID,
						buildID: buildID ?? "",
					});
				}
				if (buildID && this.activeBuild?.Status && isBuildTerminated(this.activeBuild.Status)) {
					this.$emit("open-execution-log", {
						taskID: "",
						buildID,
					});
				}
			} catch (error) {
				monitoringCaptureError(error, "Open repository execution log");
				this.taskResultsFetchStatus.error = true;
				this.taskResultsFetchStatus.message = error as string;
			} finally {
				this.taskResultsFetchStatus.loading = false;
			}
		},
		async cancelActiveTask() {
			const taskID = this.activeTask?.TaskID;
			await this.cancel.cancelTask(taskID, {
				onSent: () => {
					// @ts-ignore
					this.$posthog?.capture("task_cancelled", {
						task_id: taskID,
						build_id: this.activeBuild?.BuildID,
						status: "Cancelled",
					});
				},
			});
		},
		async cancelActiveBuild() {
			const buildID = this.activeBuild?.BuildID;
			await this.cancel.cancelBuild(buildID);
			// @ts-ignore
			this.$posthog?.capture("build_cancelled", {
				build_id: buildID,
				build_type: "Repository",
				status: "Cancelled",
			});
		},
		openRepositoryDetailsPage() {
			this.$router.push({
				name: "RepositoryDetail",
				query: { repositoryID: this.repository.RepositoryID },
			});
		},
		async discoverVariables() {
			// FIXME: use post project end point to build and run on the default core and get the variable list
			// TODO: Update this to use Modules endpoint or tasks with build only query param.
			// TODO: Consider moving this to the repo details page

			try {
				const discoveryCore = this.coresStore.getSmallestDefaultCoreForDiscoveringVariables;

				if (!discoveryCore?.CoreID) {
					throw new Error("No suitable core available for variable discovery");
				}

				const response = await sdk.builds.createFromRepository(
					this.repositoryID,
					{
						CoreID: discoveryCore.CoreID,
					},
					true,
				);
				this.lastBuildForVariableDiscovery = true;

				const buildResponse = await sdk.builds.getOne(response.BuildID);
				if (response.BuildID) {
					this.activeBuild = buildResponse as any;

					if (!this.activeBuild?.BuildID) {
						throw new Error("Build ID not returned from API");
					}

					console.log(`Build created ID: ${this.activeBuild?.BuildID}`);
					// @ts-ignore
					this.$posthog?.capture("build_created", {
						buildType: "Repository",
						buildID: this.activeBuild?.BuildID,
					});

					if (this.buildEventBusSub == undefined) {
						// Is this necessary?
						this.subscribeToBuildEventBus();
					}

					this.buildsStore.addToActiveBuildList(this.activeBuild as Build);
				} else {
					throw new Error("Unknown error: failed to create build on server");
				}
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					monitoringCaptureError(error, "Repository not found during variable discovery", {
						severity: "info",
					});
					this.$emit("repository-missing");
					return;
				}
				monitoringCaptureError(error, "Discover variables");
			}
		},
		async buildEventBusCallback(event: BuildBusEvent) {
			// @ts-ignore FIXME:
			const buildID = this.activeBuild?.BuildID;
			if (buildID && event.buildData.BuildID === buildID) {
				this.activeBuild = event.buildData;
				const buildStatus = event.buildData.Status;

				try {
					if (buildStatus && canFetchVariables(buildStatus)) {
						// fetch task variables
						this.buildsStore.fetchAndParseVariables(buildID).then((result) => {
							this.$emit("repository-variables-updated", result);
						});
					}
				} catch (error) {
					monitoringCaptureError(error, "Handle active repository task status change");
				}

				const { wasCancelledByUser: buildWasCancelledByUser } = this.cancel.handleBuildStatusUpdate(
					buildID,
					buildStatus,
				);

				const variableDiscoveryFail = isBuildFail(buildStatus) && this.lastBuildForVariableDiscovery;
				const buildTerminated = isBuildTerminated(buildStatus) && !this.lastBuildForVariableDiscovery;
				const buildFailed = isBuildFail(buildStatus) && !this.lastBuildForVariableDiscovery;
				// Skip the fetch only for codes in BUILD_ERRORS_SKIP_OUTPUT (currently just clone); all others fetch.
				const skipBuildOutput = shouldSkipBuildOutputForError(
					util.parseBuildError(event.buildData.StatusTransitions, buildStatus).code,
				);
				const successBuildExecution =
					buildStatus == "Completed" && !this.lastBuildForVariableDiscovery && !buildWasCancelledByUser;

				// PostHog: Track build lifecycle
				if (successBuildExecution) {
					// @ts-ignore
					this.$posthog?.capture("build_completed", {
						build_id: buildID,
						build_type: "Repository",
						status: "Completed",
					});
				}
				if (
					isBuildTerminated(buildStatus) &&
					buildStatus !== BuildStatusE.Completed &&
					buildStatus !== BuildStatusE.Cancelled &&
					!this.lastBuildForVariableDiscovery
				) {
					// @ts-ignore
					this.$posthog?.capture("build_failed", {
						build_id: buildID,
						build_type: "Repository",
						status: buildStatus,
					});
				}

				// Handle variable discovery failures - emit empty result if build terminated without success
				if (
					this.lastBuildForVariableDiscovery &&
					isBuildTerminated(buildStatus) &&
					!canFetchVariables(buildStatus)
				) {
					this.$emit("repository-variables-updated", []);
				}

				if (successBuildExecution && !this.activeTask?.TaskID && !this.submittingTask) {
					// Status stays Pending until submitTaskRequest sets the real one.
					this.submitTaskRequest();
				} else if (buildTerminated && this.activeTask && !this.activeTask.TaskID && !this.submittingTask) {
					// The queued task will never be submitted, so collapse the
					// optimistic Pending status to a terminal one.
					this.activeTask.Status = TaskStatusE.NotStarted;
				}
				const buildWasCancelled = buildStatus === BuildStatusE.Cancelled;
				if ((variableDiscoveryFail || buildTerminated) && !buildWasCancelled && !skipBuildOutput) {
					await this.outputsStore.retryOutputs({ buildID });
				}
				if (buildFailed && !buildWasCancelled && !skipBuildOutput) {
					this.openExecutionLog();
				}
			}
		},
		async taskEventBusCallback(event: TaskBusEvent) {
			if (this.activeTask?.TaskID && event.taskData.TaskID === this.activeTask?.TaskID) {
				this.activeTask = event.taskData;
				if (this.activeTask?.Status && isTaskTerminated(this.activeTask.Status)) {
					this.cancel.handleTaskStatusUpdate(this.activeTask.TaskID, this.activeTask.Status);

					// PostHog: Track task lifecycle
					const taskStatus = this.activeTask.Status;
					if (taskStatus === TaskStatusE.Completed) {
						// @ts-ignore
						this.$posthog?.capture("task_completed", {
							task_id: this.activeTask.TaskID,
							build_id: this.activeBuild?.BuildID,
							status: "Completed",
						});
					} else if (taskStatus !== TaskStatusE.Cancelled) {
						// @ts-ignore
						this.$posthog?.capture("task_failed", {
							task_id: this.activeTask.TaskID,
							build_id: this.activeBuild?.BuildID,
							status: taskStatus,
						});
					}

					if (this.activeTask.Status !== TaskStatusE.Cancelled) {
						// Prefetch so openExecutionLog can gate on error.
						await this.outputsStore.retryOutputs({
							taskID: this.activeTask.TaskID,
							buildID: this.activeBuild?.BuildID,
						});
						this.openExecutionLog();
					}
				}
			}
		},
		subscribeToTaskEventBus() {
			this.taskEventBusSub = this.tasksStore.taskEventBus.on(this.taskEventBusCallback);
		},
		subscribeToBuildEventBus() {
			this.buildEventBusSub = this.buildsStore.buildEventBus.on(this.buildEventBusCallback);
		},
		async getConfigFile() {
			let fileContent: undefined | string = undefined;
			let fileEncoding: undefined | string = undefined;
			this.repoConfigFileData = null;
			this.repoConfigFilePath = null;

			try {
				const configFile = await githubClient.detectSignaloidConfigFile(
					this.repositoryFullName,
					this.repository.Branch,
				);
				if (!configFile) {
					/*
					 *	No config file found in repository
					 */
					return;
				}

				if (configFile.content) {
					fileContent = configFile.content;
					fileEncoding = "base64";
				} else {
					const response = await githubClient.getRepoConfigFile(
						this.repositoryFullName,
						this.repository?.Branch,
						configFile.path,
					);

					// @ts-ignore
					fileContent = response.data.content;
					// @ts-ignore
					fileEncoding = response.data.encoding;
				}
				this.repoConfigFilePath = configFile.path;
			} catch (error) {
				// Only skip logging for expected 4xx client errors (e.g., 404 when config file doesn't exist)
				// Log server errors (5xx), network failures, and non-axios errors
				if (axios.isAxiosError(error)) {
					const status = error.response?.status;
					if (!status || status >= 500) {
						monitoringCaptureError(error, "getConfigFile:detectSignaloidConfigFile+getRepoConfigFile", {
							severity: "error",
						});
					}
				} else {
					// Non-axios errors are unexpected code errors
					monitoringCaptureError(error, "getConfigFile:detectSignaloidConfigFile+getRepoConfigFile", {
						severity: "fatal",
					});
				}
				return;
			}

			if (!fileContent) {
				console.warn("Config file was detected but content could not be retrieved");
				this.repoConfigFileData = null;
				this.repoConfigFilePath = null;
				return;
			}

			/*
			 *	Parse the Signaloid config file
			 */
			try {
				// Check if the file is base64 encoded and decode if needed
				if (fileEncoding == "base64") {
					fileContent = base64Decode(fileContent);
				}

				this.repoConfigFileData = YAML.parse(fileContent);

				// Sanitize minimum core requirements by removing invalid ones
				if (this.repoConfigFileData && "MinimumCore" in this.repoConfigFileData) {
					// Iterate through minimum core requirements
					for (const property in this.repoConfigFileData.MinimumCore) {
						const value = this.repoConfigFileData.MinimumCore[property];
						// Check if this property valid
						if (!(property in SignaloidConfigFileMinimumCoreOptions)) {
							delete this.repoConfigFileData.MinimumCore[property];
							this.repoConfigFileWarnings.push("Invalid parameter " + property);
							continue;
						}
						// Check if this property value is valid
						if (SignaloidConfigFileMinimumCoreOptions[property].type == "number") {
							if (
								value < SignaloidConfigFileMinimumCoreOptions[property].min ||
								value > SignaloidConfigFileMinimumCoreOptions[property].max
							) {
								delete this.repoConfigFileData.MinimumCore[property];
								this.repoConfigFileWarnings.push(
									"Invalid value '" +
										value +
										"' for property '" +
										property +
										"'. Value must be within range: [" +
										SignaloidConfigFileMinimumCoreOptions[property].min +
										", " +
										SignaloidConfigFileMinimumCoreOptions[property].max +
										"].",
								);
								continue;
							}
						}
						// Check if this property value is valid
						if (SignaloidConfigFileMinimumCoreOptions[property].type == "option") {
							if (!SignaloidConfigFileMinimumCoreOptions[property].options.includes(value)) {
								delete this.repoConfigFileData.MinimumCore[property];

								this.repoConfigFileWarnings.push(
									"Invalid value '" +
										value +
										"' for property '" +
										property +
										"'. Valid values are: [" +
										SignaloidConfigFileMinimumCoreOptions[property].options.join(", ") +
										"].",
								);
								continue;
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
				if (error instanceof YAMLError && error instanceof YAMLParseError) {
					/*
					 *	If this was a YAMLError most likely it's the user's
					 *	fault (or whoever's created the YAML file).
					 */
					this.repoConfigFileError = error;
					this.repoConfigFileData = null;
				} else {
					this.repoConfigFileData = null;
					this.repoConfigFilePath = null;
					// Only log non-YAMLError to Sentry
					monitoringCaptureError(error, "Get repository config file");
				}
			}
		},
	},
	computed: {
		...mapState(useDataSourcesStore, {
			defaultRepoMountConfig: "defaultMountConfig",
		}),
		...mapState(useCoresStore, {
			filteredVisibleCoresList: "filteredVisibleCoresList",
			coresSatisfyMinConfig: "coresSatisfyMinConfig",
		}),
		mergedTaskAccessState(): RemoteAccessState {
			return util.mergeAccessState(
				this.outputsStore.getTaskOutputAccessState(this.activeTask?.TaskID),
				this.taskAccessState,
			);
		},
		mergedBuildAccessState(): RemoteAccessState {
			return util.mergeAccessState(
				this.outputsStore.getBuildOutputAccessState(this.activeBuild?.BuildID),
				this.buildAccessState,
			);
		},
		hasTerminatedTaskOrBuild(): boolean {
			const hasTerminatedTask = Boolean(this.activeTask?.Status && isTaskTerminated(this.activeTask.Status));

			const hasFailedBuild = Boolean(this.activeBuild?.Status && isBuildFail(this.activeBuild.Status));

			return hasTerminatedTask || hasFailedBuild;
		},
		repoUniqueName(): string {
			if (this.remoteURLHost === "GitHub") {
				// return this.repositoryFromDB.RemoteURL.split("github.com/")[1];
				return this.repository.RemoteURL;
			}
			return this.repositoryID;
		},
		repoPlaceholderName(): string {
			if (this.remoteURLHost === "GitHub") {
				return this.repository.RemoteURL.split("github.com/")[1];
			}
			return this.repositoryFullName;
		},
		remoteURLHost(): string {
			const remoteURL = new URL(this.repository.RemoteURL);
			// FIX: Maybe returning pretty string for GitHub?
			if (remoteURL.host === "github.com") {
				return "GitHub";
			} else {
				return remoteURL.hostname;
			}
		},
		/*
		 *	Returns the "$owner/$repo" of the current repository
		 */
		repositoryFullName(): string {
			const repoRemoteURL = new URL(this.repository.RemoteURL);
			/*
			 *	Slice removes the "/" prefix, strip any trailing ".git" suffix
			 *	so GitHub API lookups resolve when repos are registered with
			 *	the ".git" suffix (e.g. via core_downloader.py --repo-url=...git).
			 */
			return repoRemoteURL.pathname.slice(1).replace(/\.git$/, "");
		},
		chosenCore(): Core | undefined {
			if (this.repository?.Core) {
				const activeCoreID = this.repository.Core;
				const chosenCore = this.filteredVisibleCoresList.find((core) => core.CoreID === activeCoreID);
				return chosenCore;
			}
			return undefined;
		},
		chosenDataSources(): DataSource[] | undefined {
			if (this.repository?.DataSources) {
				return this.repository.DataSources;
			}
			return undefined;
		},
		dataSourceChipStyle(): { icon: any; color: string; tooltip: string } {
			if (this.chosenDataSources === undefined) {
				return {
					icon: markRaw(SettingsServices16),
					color: "primary",
					tooltip: "Using default data sources specified in settings.",
				};
			} else if (this.chosenDataSources.length === 0) {
				return {
					icon: markRaw(DataError16),
					color: "grey",
					tooltip: "No data sources.",
				};
			} else {
				return {
					icon: markRaw(DataCheck16),
					color: "primary",
					tooltip: `${this.chosenDataSources.length} data source${
						this.chosenDataSources.length > 1 ? "s" : ""
					} mounted.`,
				};
			}
		},
		buildDirectoryPrettyString(): string {
			if (this.repository.BuildDirectory === "") {
				return "./";
			} else if (this.repository.BuildDirectory) {
				return this.repository.BuildDirectory;
			} else {
				return "-";
			}
		},
		activeCore(): Core | undefined {
			if (this.coresStore.loadingCores || this.coresStore.filteredVisibleCoresList.length === 0) {
				return undefined;
			}

			if (this.chosenCore) {
				return this.chosenCore;
			}

			const defaultCore = this.coresStore.defaultCore;
			if (defaultCore?.CoreID) {
				return defaultCore;
			}
			console.warn("No default core available for repository");
			return undefined;
		},
		coreUsedForExecution(): string | undefined {
			return this.repository?.Core ?? this.coresStore.defaultCoreID;
		},
		coreChipStyle(): ChipStyle {
			// If there is no min core config
			if (!this.repoConfigFileData?.MinimumCore) {
				// the core is specified in the repo
				if (this.repository?.Core) {
					return { color: "primary", outlined: true, textColor: "primary" };
				} else {
					// else the repo will use default core
					return { color: "grey", outlined: true, textColor: "grey" };
				}
			}

			if (!this.activeCore) {
				return { color: "grey", outlined: true, textColor: "grey" };
			}
			// get the default core
			return this.activeCore?.CoreID &&
				!this.coresSatisfyMinConfig(this.repoConfigFileData.MinimumCore)[this.activeCore.CoreID]
				? { color: "warning", outlined: true, textColor: "warning" }
				: { color: "primary", outlined: true, textColor: "primary" };
		},
		coreListTextColor(): string | undefined {
			if (!this.repoConfigFileData?.MinimumCore || !this.activeCore?.CoreID) {
				return undefined;
			}

			return !this.coresSatisfyMinConfig(this.repoConfigFileData.MinimumCore)[this.activeCore.CoreID]
				? "sgnl-text-color-disabled"
				: "sgnl-text-color-brand";
		},
	},
	created() {
		this.coresStore.getCoresFilteredCoresByUserTier();
	},
	async beforeMount() {
		const availableCoreCoreID = this.coresStore.resetCoreIdIfRestricted(this.repository.Core);
		if (availableCoreCoreID != this.repository.Core) {
			/*
			 *	Force-reset the repository Core if the configured CoreID is not
			 *	available or restricted.
			 */
			await this.repositoriesStore.setRepositoryCoreID(
				this.repositoryID,
				availableCoreCoreID,
				this.coreUpdateStatus,
			);
		}

		// Always fetch repo data before mount.
		this.selectedBuildDirectory = this.repository.BuildDirectory;

		/*
		 *	Wait until GitHub details are fetched and then fetch repositories
		 *	(to make sure auth is correctly set)
		 */
		await this.getRepoDataFromRemote();

		// TODO: Check for active tasks matching this repo ID, and add sub if matches
	},
	beforeUnmount() {
		/*
		 *	Unsubscribe from the task even bus on dismount
		 */
		if (this.taskEventBusSub !== undefined) {
			this.taskEventBusSub();
		}
		if (this.buildEventBusSub !== undefined) {
			this.buildEventBusSub();
		}
	},
});
</script>

<style lang="scss" scoped>
.config-file-icon {
	flex-shrink: 0;
	color: var(--sgnl-brand-icon);
}

/* Preserve newlines in the YAML parser error message. */
.yaml-error-message {
	white-space: pre-line;
}

.repo-error-message {
	border-radius: var(--sgnl-border-radius-6);
	border: var(--sgnl-border-width-1) solid var(--sgnl-alert-warning-border);
	background: var(--sgnl-alert-warning-background-primary);
	font-size: var(--sgnl-font-size-paragraph-s);
	font-weight: 400;
	line-height: var(--sgnl-font-line-height-paragraph-s);
}

.error-state {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.error-state__details {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.repo-error-icon {
	color: var(--sgnl-alert-warning-icon);
	flex-shrink: 0;
}

.truncated {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 150px;
}

@media (max-width: 599px) {
	.resource-card__config :deep(.v-btn) {
		width: auto;
	}

	.resource-card__footer > .sgnl-flex {
		flex-wrap: wrap;
	}

	.resource-card__footer > .sgnl-flex > .v-spacer {
		display: none;
	}

	.resource-card__footer .v-chip.v-chip {
		width: auto;
		flex: 0 0 auto;
	}

	.resource-card__footer :deep(.v-btn--variant-outlined) {
		width: auto;
		flex: 1;
	}

	.resource-card__footer > .sgnl-flex > span {
		display: block;
		width: 100%;
	}
}
</style>
